import { JSONContent } from '@tiptap/core';
import {
  fallbackCopy,
  newImagePinComponent,
  newSize,
  newTextPinComponent,
  newVector,
  newVideoPinComponent,
} from '../helpers';
import { LU_PIN_DIMENSIONS, SlidePinLayoutTemplate, TextPinComponent } from '../models';
import { stringToProsemirror } from '../tip-tap';

export type TextWithSettings = {
  content?: JSONContent;
  settings?: Partial<TextPinComponent['settings']>;
};

export function slideBlankLayout(settings: { backgroundColor?: string } = {}): SlidePinLayoutTemplate {
  return {
    key: 'BLANK',
    backgroundColor: settings.backgroundColor,
    layoutParts: {},
    pinComponents: [],
  };
}

export function slideImageOnly({
  backgroundColor,
  backgroundImage,
  imageTransparency,
}: {
  backgroundColor?: string;
  backgroundImage?: string;
  imageTransparency?: number;
}): SlidePinLayoutTemplate {
  return {
    key: 'IMAGE_ONLY',
    backgroundColor,
    layoutParts: {},
    pinComponents: [],
    backgroundImage,
    backgroundTransparency: imageTransparency,
  };
}

export function slideCoverLayout({
  title,
  body,
  backgroundColor,
}: {
  title?: TextWithSettings;
  body?: TextWithSettings;
  backgroundColor?: string;
}): SlidePinLayoutTemplate {
  return {
    key: 'COVER',
    backgroundColor,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(896, 234),
        content:
          title?.content ||
          stringToProsemirror(fallbackCopy['cover.title'], {
            bold: true,
          }),
        settings: {
          textStyleKey: 'title',
          horizontalAlignment: 'center',
          verticalAlignment: 'end',
          typographyKey: 'title',
          ...title?.settings,
        },
        order: 'a0',
      }),
      BODYTEXT1: newTextPinComponent({
        position: newVector(32, 274),
        size: newSize(896, 234),
        content: body?.content || stringToProsemirror(fallbackCopy['cover.body']),
        settings: {
          textStyleKey: 'body',
          typographyKey: 'subheadline',
          horizontalAlignment: 'center',
          verticalAlignment: 'baseline',
          ...body?.settings,
        },
        order: 'a1',
      }),
    },
    pinComponents: [],
  };
}

export function slideTitleWithBodytextLayout({
  title,
  body,
  backgroundColor,
}: {
  title?: TextWithSettings;
  body?: TextWithSettings;
  backgroundColor?: string;
}): SlidePinLayoutTemplate {
  return {
    key: 'TITLE_TOP_WITH_BODYTEXT',
    backgroundColor,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(896, 60),
        content:
          title?.content ||
          stringToProsemirror(fallbackCopy['slide.title'], {
            bold: true,
          }),
        settings: {
          typographyKey: 'headline',
          ...title?.settings,
        },
        order: 'a0',
      }),
      BODYTEXT1: newTextPinComponent({
        position: newVector(32, 124),
        size: newSize(896, 384),
        content: body?.content || stringToProsemirror(fallbackCopy['slide.body']),
        settings: { typographyKey: 'normalText', ...body?.settings },
        order: 'a1',
      }),
    },
    pinComponents: [],
  };
}

export function slideImageTopLayout({
  title,
  imageUrl = '',
  backgroundColor,
  imageTransparency,
}: {
  title?: TextWithSettings;
  imageUrl?: string;
  backgroundColor?: string;
  imageTransparency?: number;
}): SlidePinLayoutTemplate {
  return {
    key: 'IMAGE_TOP',
    backgroundColor,
    layoutParts: {
      IMAGE1: newImagePinComponent({
        position: newVector(32, 32),
        size: newSize(896, 384),
        order: 'a0',
        url: imageUrl,
        settings: {
          transparency: imageTransparency,
        },
      }),
      TITLE: newTextPinComponent({
        position: newVector(32, 448),
        size: newSize(896, 60),
        content:
          title?.content ||
          stringToProsemirror(fallbackCopy['slide.title'], {
            bold: true,
          }),
        settings: { typographyKey: 'headline', ...title?.settings },
        order: 'a1',
      }),
    },
    pinComponents: [],
  };
}

export function slideImageLeftLayout({
  title,
  body,
  backgroundColor,
  imageUrl = '',
  imageTransparency,
}: {
  title?: TextWithSettings;
  body?: TextWithSettings;
  backgroundColor?: string;
  imageUrl?: string;
  imageTransparency?: number;
}): SlidePinLayoutTemplate {
  return {
    key: 'IMAGE_LEFT',
    backgroundColor,
    layoutParts: {
      IMAGE1: newImagePinComponent({
        position: newVector(0, 0),
        size: newSize(480, 540),
        order: 'a0',
        url: imageUrl,
        settings: {
          transparency: imageTransparency,
        },
      }),
      TITLE: newTextPinComponent({
        position: newVector(512, 32),
        size: newSize(416, 82),
        content:
          title?.content ||
          stringToProsemirror(fallbackCopy['slide.title'], {
            bold: true,
          }),
        settings: {
          typographyKey: 'headline',
          ...title?.settings,
        },
        order: 'a1',
      }),
      BODYTEXT1: newTextPinComponent({
        position: newVector(512, 130),
        size: newSize(416, 378),
        content: body?.content || stringToProsemirror(fallbackCopy['slide.body']),
        settings: {
          typographyKey: 'normalText',
          ...body?.settings,
        },
        order: 'a2',
      }),
    },
    pinComponents: [],
  };
}

export function slideImageRightLayout({
  title,
  body,
  backgroundColor,
  imageUrl = '',
  imageTransparency,
}: {
  title?: TextWithSettings;
  body?: TextWithSettings;
  backgroundColor?: string;
  imageUrl?: string;
  imageTransparency?: number;
}): SlidePinLayoutTemplate {
  return {
    key: 'IMAGE_RIGHT',
    backgroundColor,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(416, 82),
        content:
          title?.content ||
          stringToProsemirror(fallbackCopy['slide.title'], {
            bold: true,
          }),
        settings: { typographyKey: 'headline', ...title?.settings },
        order: 'a0',
      }),
      BODYTEXT1: newTextPinComponent({
        position: newVector(32, 130),
        size: newSize(416, 378),
        content: body?.content || stringToProsemirror(fallbackCopy['slide.body']),
        settings: { typographyKey: 'normalText', ...body?.settings },
        order: 'a1',
      }),
      IMAGE1: newImagePinComponent({
        position: newVector(480, 0),
        size: newSize(480, 540),
        order: 'a2',
        url: imageUrl,
        settings: {
          transparency: imageTransparency,
        },
      }),
    },
    pinComponents: [],
  };
}
export function slideDoubleColumnImageLayout({
  textOne,
  textTwo,
  backgroundColor,
  imageOne = '',
  imageTwo = '',
  imageTransparencyOne,
  imageTransparencyTwo,
}: {
  textOne?: TextWithSettings;
  textTwo?: TextWithSettings;
  backgroundColor?: string;
  imageOne?: string;
  imageTwo?: string;
  imageTransparencyOne?: number;
  imageTransparencyTwo?: number;
}): SlidePinLayoutTemplate {
  return {
    key: 'BODYTEXT_AND_IMAGE_TWO_COLUMNS',
    backgroundColor,
    layoutParts: {
      IMAGE1: newImagePinComponent({
        position: newVector(32, 32),
        size: newSize(432, 436),
        order: 'a1',
        url: imageOne,
        settings: {
          transparency: imageTransparencyOne,
        },
      }),
      IMAGE2: newImagePinComponent({
        position: newVector(496, 32),
        size: newSize(432, 436),
        order: 'a2',
        url: imageTwo,
        settings: {
          transparency: imageTransparencyTwo,
        },
      }),
      BODYTEXT1: newTextPinComponent({
        position: newVector(32, 468),
        size: newSize(432, 40),
        content: textOne?.content || stringToProsemirror(fallbackCopy['slide.body']),
        settings: { typographyKey: 'normalText', ...textOne?.settings },
        order: 'a3',
      }),
      BODYTEXT2: newTextPinComponent({
        position: newVector(496, 468),
        size: newSize(432, 40),
        content: textTwo?.content || stringToProsemirror(fallbackCopy['slide.body']),
        settings: { typographyKey: 'normalText', ...textTwo?.settings },
        order: 'a4',
      }),
    },
    pinComponents: [],
  };
}
export function slideDoubleColumnTextLayout({
  title,
  subTitleOne,
  textOne,
  subTitleTwo,
  textTwo,
  backgroundColor,
}: {
  title?: TextWithSettings;
  subTitleOne?: TextWithSettings;
  textOne?: TextWithSettings;
  subTitleTwo?: TextWithSettings;
  textTwo?: TextWithSettings;
  backgroundColor?: string;
}): SlidePinLayoutTemplate {
  return {
    key: 'BODYTEXT_TWO_COLUMNS',
    backgroundColor,
    layoutParts: {
      TITLE: newTextPinComponent({
        position: newVector(32, 32),
        size: newSize(896, 56),
        content: title?.content || stringToProsemirror(fallbackCopy['slide.body']),
        settings: { typographyKey: 'headline', ...title?.settings },
        order: 'a0',
      }),
      SUBTITLE1: newTextPinComponent({
        position: newVector(32, 120),
        size: newSize(432, 45),
        content: subTitleOne?.content || stringToProsemirror(fallbackCopy['column.title']),
        settings: { typographyKey: 'subheadline', ...subTitleOne?.settings },
        order: 'a1',
      }),
      BODYTEXT1: newTextPinComponent({
        position: newVector(32, 175),
        size: newSize(432, 335),
        content: textOne?.content || stringToProsemirror(fallbackCopy['column.body']),
        settings: { typographyKey: 'normalText', ...textOne?.settings },
        order: 'a1',
      }),
      SUBTITLE2: newTextPinComponent({
        position: newVector(496, 120),
        size: newSize(432, 45),
        content: subTitleTwo?.content || stringToProsemirror(fallbackCopy['column.title']),
        settings: { typographyKey: 'subheadline', ...subTitleTwo?.settings },
        order: 'a1',
      }),
      BODYTEXT2: newTextPinComponent({
        position: newVector(496, 175),
        size: newSize(432, 335),
        content: textTwo?.content || stringToProsemirror(fallbackCopy['column.body']),
        settings: { typographyKey: 'normalText', ...textTwo?.settings },
        order: 'a1',
      }),
    },
    pinComponents: [],
  };
}

export function slideFullSizeVideoLayout({ backgroundColor }: { backgroundColor?: string }): SlidePinLayoutTemplate {
  return {
    key: 'VIDEO_ONLY',
    backgroundColor,
    layoutParts: {
      VIDEO: newVideoPinComponent({
        size: newSize(LU_PIN_DIMENSIONS.width, LU_PIN_DIMENSIONS.height),
        position: newVector(0, 0),
      }),
    },
    pinComponents: [],
  };
}
